import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Bike, ShoppingCart, Wrench, Store,
  ShieldCheck, MapPin, Award, Leaf, Wallet, Star, Check,
  Users, Users2, Target, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useTestRide } from "../components/TestRideContext";
import { PRODUCTS } from "../data/products";
import ProductCard from "../components/ProductCard";
import { submitLead } from "../lib/leadsApi";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const HERO_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_bike-catalog-9/artifacts/25z3dct9_Home%20hero.jpg";

export default function Home() {
  const { openModal } = useTestRide();

  return (
    <div data-testid="home-page" className="bg-white">
      {/* ─────────────────  HERO  ───────────────── */}
      <section className="relative bg-white overflow-hidden">
        {/* Desktop: photo occupies the right side of the viewport, blending with the page */}
        <div
          aria-hidden
          className="hidden lg:block absolute inset-y-0 right-0 w-[64%] xl:w-[62%] pointer-events-none"
        >
          <img
            src={HERO_IMG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "100% 50%" }}
            loading="eager"
          />
          {/* left-edge feather so the image blends into the white background */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white via-white/60 to-transparent" />
        </div>

        <div className="container-x relative">
          <div className="grid lg:grid-cols-12 gap-6 items-center min-h-[86vh] py-14 lg:py-20">
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } }}
              className="lg:col-span-5 xl:col-span-5 max-w-xl lg:pr-6"
            >
              <motion.h1
                variants={fadeUp}
                className="font-outfit font-bold text-slate-900 tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[54px] xl:text-[62px]"
              >
                Electric bicycles for work, commute and everyday earning
                <span className="text-[#15B47E]">.</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-slate-600 mt-6 text-base sm:text-lg leading-relaxed max-w-xl"
              >
                Rent, buy and service eBikeGo electric bicycles through high-street mobility stores built for gig workers, commuters, businesses and future franchise partners.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-8 flex flex-wrap xl:flex-nowrap items-center gap-2"
              >
                <Link
                  to="/rent"
                  data-testid="hero-rent-cta"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap bg-[#2A8BFF] hover:bg-[#1E6FE5] text-white font-semibold text-[13px] xl:text-[14px] px-4 py-2.5 rounded-xl shadow-[0_6px_18px_rgba(42,139,255,0.25)] hover:shadow-[0_10px_24px_rgba(42,139,255,0.35)] transition-all"
                >
                  <Bike size={16} /> Rent an E-Cycle
                </Link>
                <Link
                  to="/products"
                  data-testid="hero-buy-cta"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap bg-white text-slate-900 border border-slate-200 hover:border-slate-900 font-semibold text-[13px] xl:text-[14px] px-4 py-2.5 rounded-xl transition-all"
                >
                  <ShoppingCart size={16} /> Buy an E-Cycle
                </Link>
                <Link
                  to="/service"
                  data-testid="hero-service-cta"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap bg-white text-slate-900 border border-slate-200 hover:border-slate-900 font-semibold text-[13px] xl:text-[14px] px-4 py-2.5 rounded-xl transition-all"
                >
                  <Wrench size={16} /> Service My E-Cycle
                </Link>
              </motion.div>

              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-slate-700"
              >
                {["Retail", "Rental", "Service", "Franchise-ready"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#15B47E]" /> {t}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* spacer so the container's right side lines up with the bleed image */}
            <div className="hidden lg:block lg:col-span-7" />
          </div>

          {/* Mobile: image below content, full-bleed */}
          <div className="lg:hidden -mx-6 sm:-mx-10 mt-2">
            <img
              src={HERO_IMG}
              alt="eBikeGo storefront — rent, buy and service"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ─────────────────  WHAT WE DO  ───────────────── */}
      <section className="section-padding relative bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow">What we do</p>
              <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[0.98] text-slate-900">
                Rent, buy and service —<br />
                <span className="text-slate-400">all under one roof.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              <p>
                A complete e-mobility ecosystem built for Indian streets. From flexible rentals for gig workers to premium e-cycles for daily riders, plus the service network to keep every ride moving.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-10 sm:mt-16">
            {[
              { num: "01", title: "Rent",      icon: Bike,        to: "/rent",             color: "#2A8BFF", soft: "#EAF1FE",
                text: "Flexible daily, weekly and monthly access for gig workers, commuters and business users." },
              { num: "02", title: "Buy",       icon: ShoppingCart, to: "/products",         color: "#15B47E", soft: "#E8FAF2",
                text: "Own a reliable e-cycle for city mobility, savings and everyday convenience." },
              { num: "03", title: "Service",   icon: Wrench,      to: "/service",          color: "#7C3AED", soft: "#F1ECFE",
                text: "Trusted service support, battery care, repairs and maintenance under one roof." },
              { num: "04", title: "Franchise", icon: Store,       to: "/about#franchise",  color: "#0E9488", soft: "#CCFBF1",
                text: "Open a compact high-street mobility store designed for local demand and future growth." },
            ].map((p, i) => (
              <PillarCard key={p.num} pillar={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  STATS BANNER  ───────────────── */}
      <section className="relative bg-gradient-to-br from-[#1E6FE5] to-[#2A8BFF] text-white py-14 lg:py-16">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {[
            { icon: Users2,     v: "10,000+", l: "Happy Riders" },
            { icon: Bike,       v: "25+",     l: "E-Cycle Models" },
            { icon: Target,     v: "100+",    l: "Cities Covered" },
            { icon: BadgeCheck, v: "2 Years", l: "Warranty Support" },
          ].map(({ icon: Icon, v, l }) => (
            <div key={l} className="flex items-center gap-4">
              <Icon size={40} strokeWidth={1.5} className="shrink-0 text-white/95" />
              <div>
                <p className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight leading-none text-white">{v}</p>
                <p className="text-xs sm:text-sm mt-2 font-semibold text-white/90">{l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────  FEATURED PRODUCTS  ───────────────── */}
      <section className="section-padding relative bg-[#F6F8FB] border-y border-slate-200">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="eyebrow">The line-up</p>
              <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[0.98] text-slate-900">
                Choose the right e-cycle <br />
                <span className="text-slate-400">for your ride.</span>
              </h2>
            </div>
            <Link to="/products" className="btn-outline" data-testid="featured-explore-all">
              Explore all models <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRODUCTS.slice(0, 6).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  WHY CHOOSE EBIKEGO  ───────────────── */}
      <section className="section-padding relative bg-white" data-testid="why-choose">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow">Why choose eBikeGo</p>
              <h2 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[0.98] text-slate-900">
                Trusted by riders <br />
                <span className="text-slate-400">across India.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              <p>
                Six reasons riders, gig fleets and franchise partners come back to eBikeGo — from manufacturer-backed quality to a store-led service network that keeps every ride moving.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {[
              { icon: ShieldCheck, title: "Manufacturer-backed Quality", text: "Lifetime frame warranty and 2-year motor cover on every eBikeGo." },
              { icon: MapPin,      title: "Made for India",              text: "Tuned for potholes, monsoons and city gridlock — Indian roads, Indian rides." },
              { icon: Wrench,      title: "Store-led Service",           text: "Walk-in, drop-off or pickup at 100+ authorised service points." },
              { icon: Award,       title: "Lower Cost of Ownership",     text: "30–50% cheaper than a petrol commute — no fuel, no queues, no fuss." },
              { icon: Users,       title: "Empowering Everyday Mobility", text: "Predictable rentals, free monthly service and rent-to-own pathways for earning riders." },
              { icon: Leaf,        title: "Sustainable Mobility",        text: "Two-wheeler distances without the emissions, noise or petrol dependency." },
            ].map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#2A8BFF] hover:shadow-[0_14px_36px_rgba(11,19,32,0.06)] hover:-translate-y-1 transition-all"
              >
                <span className="w-11 h-11 rounded-xl bg-[#EAF1FE] text-[#1E6FE5] inline-flex items-center justify-center">
                  <Icon size={20} strokeWidth={2} />
                </span>
                <h3 className="font-outfit font-bold text-xl mt-5 text-slate-900">{title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────  MOBILITY + SUBSCRIBE (Home only, compact)  ───────────────── */}
      <section data-testid="mobility-section" className="bg-white py-16 lg:py-20 border-t border-slate-200">
        <div className="container-x">
          {/* Row 1: mobility banner — 3 columns, tight */}
          <div className="grid lg:grid-cols-[1.15fr_1fr_1fr] gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 flex flex-col justify-between">
              <div>
                <h3 className="font-outfit text-3xl lg:text-[34px] font-bold tracking-tight leading-[1.1] text-slate-900">
                  Mobility that <span className="text-[#2A8BFF]">moves</span> you forward.
                </h3>
                <p className="text-slate-500 text-sm mt-3 max-w-sm">
                  Premium e-cycles, trusted by thousands across India.
                </p>
              </div>
              <div className="mt-5 flex items-center gap-4 flex-wrap">
                <Link
                  to="/products"
                  data-testid="mobility-know-more"
                  className="inline-flex items-center gap-2 bg-[#2A8BFF] hover:bg-[#1E6FE5] text-white font-semibold text-[13px] px-5 py-2.5 rounded-full transition-colors"
                >
                  Know More
                </Link>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <Star size={13} className="text-[#15B47E] fill-[#15B47E]" />
                  Rated <b className="text-slate-900">4.8/5</b> on Trustpilot
                </span>
              </div>
            </div>

            {/* Go Green */}
            <div
              data-testid="mobility-go-green"
              className="rounded-2xl p-6 flex flex-col justify-between overflow-hidden"
              style={{ background: "linear-gradient(180deg,#E8FAF2 0%,#F4FCF8 100%)" }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-outfit font-bold text-xl text-slate-900">Go Green</h4>
                  <p className="text-slate-600 text-sm mt-2 leading-snug">Zero emissions. Cleaner air. Drive the change.</p>
                </div>
                <span className="w-10 h-10 rounded-xl bg-white/80 inline-flex items-center justify-center text-[#15B47E] shrink-0">
                  <Leaf size={20} strokeWidth={2.2} />
                </span>
              </div>
              <Link
                to="/about#mission"
                className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#15B47E] hover:text-[#0F9B6A]"
              >
                Learn More <ArrowUpRight size={14} />
              </Link>
            </div>

            {/* Save More */}
            <div
              data-testid="mobility-save-more"
              className="rounded-2xl p-6 flex flex-col justify-between overflow-hidden"
              style={{ background: "linear-gradient(180deg,#EAF1FE 0%,#F5F9FF 100%)" }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-outfit font-bold text-xl text-slate-900">Save More</h4>
                  <p className="text-slate-600 text-sm mt-2 leading-snug">Save on fuel, parking &amp; maintenance. Smarter choice, bigger savings.</p>
                </div>
                <span className="w-10 h-10 rounded-xl bg-white/80 inline-flex items-center justify-center text-[#2A8BFF] shrink-0">
                  <Wallet size={20} strokeWidth={2.2} />
                </span>
              </div>
              <Link
                to="/rent"
                className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#2A8BFF] hover:text-[#1E6FE5]"
              >
                Learn More <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* Row 2: Subscribe — compact single row */}
          <div className="mt-6 rounded-2xl bg-[#F6F8FB] border border-slate-200 p-6 lg:p-7 grid lg:grid-cols-[1.15fr_1.85fr] items-center gap-6">
            <div>
              <h3 className="font-outfit text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">
                Stay in the loop with eBikeGo!
              </h3>
              <p className="text-slate-500 text-sm mt-2">
                New arrivals, offers and everything electric.
              </p>
            </div>
            <SubscribeForm />
          </div>
        </div>
      </section>
      <section className="relative bg-[#F6F8FB] border-t border-slate-200 py-24 overflow-hidden">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow">The next step</p>
            <h2 className="font-outfit text-4xl lg:text-6xl font-bold mt-4 leading-[0.98] text-slate-900">
              Ready to ride with eBikeGo?
            </h2>
            <p className="text-slate-600 mt-5 max-w-lg">
              Book a 15-minute test ride at your nearest store. We'll pick the right model for your route, your budget and your weekend plans.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Book a Test Ride", action: () => openModal(), primary: true },
              { label: "Buy an E-Cycle", to: "/products" },
              { label: "Rent Now", to: "/rent" },
              { label: "Open a Store", to: "/about#franchise" },
            ].map((a, i) =>
              a.to ? (
                <Link
                  key={i}
                  to={a.to}
                  className="block bg-white border border-slate-200 hover:border-[#2A8BFF] hover:shadow rounded-2xl p-6 group transition-all"
                  data-testid={`final-cta-${i}`}
                >
                  <p className="font-outfit font-bold text-lg text-slate-900">{a.label}</p>
                  <ArrowUpRight size={18} className="mt-2 text-[#2A8BFF] group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
                </Link>
              ) : (
                <button
                  key={i}
                  onClick={a.action}
                  className="text-left block bg-gradient-to-br from-[#2A8BFF] to-[#1E6FE5] text-white rounded-2xl p-6 group hover:shadow-xl transition-all"
                  data-testid={`final-cta-${i}`}
                >
                  <p className="font-outfit font-bold text-lg">{a.label}</p>
                  <ArrowUpRight size={18} className="mt-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
                </button>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function PillarCard({ pillar, index }) {
  const { num, title, text, to, icon: Icon, color, soft } = pillar;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: index * 0.07 }}
      className="group relative bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 flex flex-col hover:border-slate-300 hover:shadow-[0_14px_36px_rgba(11,19,32,0.06)] transition-all"
    >
      {/* Vertical color tile with icon — proportioned square-ish so the icon fills nicely */}
      <div
        className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6"
        style={{ background: soft }}
      >
        <Icon className="w-7 h-7 sm:w-10 sm:h-10" strokeWidth={2} style={{ color }} />
      </div>

      <p
        className="font-outfit text-[10px] sm:text-xs font-bold tracking-[0.18em]"
        style={{ color }}
      >
        {num}
      </p>
      <h3 className="font-outfit font-bold text-lg sm:text-2xl mt-1 sm:mt-2 text-slate-900">{title}</h3>
      <span
        className="block w-6 sm:w-8 h-[2px] sm:h-[3px] rounded-full mt-2 sm:mt-3"
        style={{ background: color }}
      />

      <p className="text-[12px] sm:text-sm text-slate-600 mt-3 sm:mt-5 leading-relaxed flex-1">{text}</p>

      <div className="mt-4 sm:mt-6 flex items-center justify-end">
        <Link
          to={to}
          aria-label={`${title} — learn more`}
          data-testid={`pillar-${title.toLowerCase()}`}
          className="w-9 h-9 sm:w-11 sm:h-11 rounded-full border border-slate-200 text-slate-700 inline-flex items-center justify-center transition-all group-hover:border-slate-900 group-hover:bg-slate-900 group-hover:text-white"
        >
          <ArrowRight size={14} className="sm:hidden" />
          <ArrowRight size={16} className="hidden sm:block" />
        </Link>
      </div>
    </motion.div>
  );
}

/* Compact subscribe form for the Home mobility banner */
function SubscribeForm() {
  const [email, setEmail] = useState("");
  const [agree, setAgree] = useState(true);

  const submit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!agree) {
      toast.error("Please tick the consent box to subscribe.");
      return;
    }
    toast.success("Subscribed! Welcome to the eBikeGo loop.");
    submitLead({ type: "newsletter", email });
    setEmail("");
  };

  return (
    <form onSubmit={submit} className="w-full" data-testid="subscribe-form">
      <div className="flex items-stretch bg-white border border-slate-200 rounded-full overflow-hidden shadow-[0_4px_14px_rgba(11,19,32,0.05)]">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          data-testid="subscribe-input"
          className="flex-1 min-w-0 bg-transparent px-4 sm:px-5 py-3.5 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none"
        />
        <button
          type="submit"
          data-testid="subscribe-submit"
          className="shrink-0 bg-[#2A8BFF] hover:bg-[#1E6FE5] text-white font-bold text-[12px] sm:text-[13px] uppercase tracking-[0.06em] px-4 sm:px-7 transition-colors"
        >
          Subscribe
        </button>
      </div>
      <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          data-testid="subscribe-consent"
          className="sr-only"
        />
        <span className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center transition ${agree ? "bg-[#2A8BFF] border-[#2A8BFF]" : "bg-white border-slate-300"}`}>
          {agree && <Check size={11} className="text-white" strokeWidth={3} />}
        </span>
        I agree to receive emails from eBikeGo.
      </label>
    </form>
  );
}

